import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"
import { attachChatScroll } from "../utils/chat_scroll"
import { showSystemToast } from "../utils/system_toast"

// Safety cap: never poll forever. The server sweeps stale placeholders after
// 5 minutes, so a final refresh at this point will still pick up the error
// bubble.
const MAX_POLL_MS = 10 * 60 * 1000
// Poll fast for the first FAST_TICKS ticks (a run actively streaming text),
// then back off — a long-idle Jane Live thread doesn't need sub-second
// polling for the rest of the 10-minute cap.
const FAST_TICKS = 25
const FAST_POLL_MS = 1200
// Right after a send the placeholder says "Responding…" (a typing indicator,
// no text yet). The first words are what the reader is waiting for, so poll
// much tighter until they land — for WAIT_TICKS ticks at most, then fall back
// to the normal cadence (a slow tool-using reply doesn't need 350ms polling
// for its whole run). These ticks don't count against FAST_TICKS.
const WAIT_POLL_MS = 350
const WAIT_TICKS = 40
const SLOW_POLL_MS = 2500
const RUN_POLL_MS = 4000

// Thrown for a poll response that can never succeed by retrying: a non-2xx
// status, or a same-origin fetch that got redirected somewhere that isn't
// the JSON endpoint it asked for (a session that expired mid-poll lands on
// the sign-in page, still HTTP 200). Distinguishes these from a genuine
// network blip, which the existing transient retry still covers.
class TerminalPollError extends Error {}

export default class extends Controller {
    static values = {
        conversationId: Number,
        url: String,
        frameUrl: String,
        live: { type: Boolean, default: false }
    }

    connect() {
        this._stopped = false
        this._ticks = 0
        this._waitTicks = 0
        this._startedAt = Date.now()
        this._pausedForVisibility = false
        this._settleScroll({ reset: true })
        this._onVisibility = this._onVisibility.bind(this)
        document.addEventListener("visibilitychange", this._onVisibility)
        // A reaction on Jane's latest reply starts a reply of its own over a
        // fetch (utils/reaction_reply.js) — wake up for it like a send.
        this._onReactionReply = (event) => {
            if (event.detail?.conversationId === this.conversationIdValue) this.restart()
        }
        document.addEventListener("reactions:reply-started", this._onReactionReply)
        // A re-render held back while a reaction picker was open runs the
        // moment it closes (see _refreshChatFrame).
        this._onPickerClosed = () => this._flushDeferredRefresh()
        this.element.addEventListener("emoji-picker:closed", this._onPickerClosed)
        // Jane Live streams proposal cards without a pending reply bubble —
        // keep polling so new mail surfaces without a manual refresh.
        if (!this.liveValue && !this._hasPending()) return
        this._subscribeActivity()
        this._poll()
    }

    disconnect() {
        this._stopped = true
        if (this._timer) clearTimeout(this._timer)
        this._unsubscribeActivity()
        document.removeEventListener("visibilitychange", this._onVisibility)
        document.removeEventListener("reactions:reply-started", this._onReactionReply)
        this.element.removeEventListener("emoji-picker:closed", this._onPickerClosed)
        this._chatScroll?.destroy()
        this._chatScroll = null
    }

    // --- Tab visibility: a hidden tab has no reader to show a reply to, so
    // stop spending fetches on it. Mirrors assistant_hero_controller's own
    // _onVisibility (same event, same battery motivation, a different
    // resource) — the difference is what "paused" means: there, frozen CSS
    // and rAF; here, no network calls until someone is looking again.
    _onVisibility() {
        if (document.hidden || !this._pausedForVisibility) return
        // Coming back from hidden: don't wait out whatever delay was queued
        // (up to SLOW_POLL_MS) — the reader just looked, catch them up now.
        this._pausedForVisibility = false
        if (this._timer) clearTimeout(this._timer)
        this._poll()
    }

    // Kick a poll for a run that started OUT OF BAND. The Jane sidebar hosts a
    // persistent thread and composes via fetch, so nothing re-connects this
    // controller with a pending bubble in the DOM — without this the reply would
    // only appear on the next page load. Idempotent: a poll already in flight is
    // cancelled and restarted from a clean clock.
    restart() {
        if (!this.hasConversationIdValue) return

        if (this._timer) clearTimeout(this._timer)
        this._stopped = false
        this._ticks = 0
        this._waitTicks = 0
        this._startedAt = Date.now()
        this._fingerprint = null
        this._deferredRefresh = null
        this._settleScroll({ reset: true })
        this._subscribeActivity()
        this._refreshChatFrame({ stop: false })
        this._poll()
    }

    // Pin a message that is not a turn yet: the composer draws your message and
    // Jane's typing dots the moment you send (assistant_compose, hosted panes),
    // ahead of the request being answered. Same policy as a real send — the
    // message goes to the top and the reply grows below it. The first render
    // with the real user turn re-arms the pin on that (see _settleScroll).
    pinTo(element) {
        const scroller = this._scroller()
        if (!scroller || !element) return
        if (!this._chatScroll || this._chatScrollFor !== scroller) {
            this._chatScroll?.destroy()
            this._chatScrollFor = scroller
            this._chatScroll = attachChatScroll(scroller, { anchor: "top" })
        }
        this._anchoredTurnId = null
        this._chatScroll.anchorTo(element).reset()
    }

    // The server announces "talking" on the owner's AssistantActivityChannel
    // the moment a reply's first words are written (Assistant::LiveReply).
    // Refresh the chat frame right then instead of waiting out the poll tick.
    // Polling stays as the fallback: a socket that is down, or an event that
    // is lost, costs nothing but the old latency.
    _subscribeActivity() {
        if (this._subscription || typeof createConsumer !== "function") return
        try {
            this._consumer = createConsumer()
            this._subscription = this._consumer.subscriptions.create(
                { channel: "AssistantActivityChannel" },
                { received: (data) => this._onActivity(data) }
            )
        } catch (_error) {
            // Polling alone still works.
        }
    }

    _unsubscribeActivity() {
        try {
            this._subscription?.unsubscribe()
            this._consumer?.disconnect()
        } catch (_error) { /* already gone */ }
        this._subscription = null
        this._consumer = null
    }

    _onActivity(data) {
        if (this._stopped || data?.type !== "activity" || data.state !== "talking") return
        if (Number(data.conversation_id) !== this.conversationIdValue) return
        if (document.hidden) return
        this._refreshChatFrame({ stop: false })
    }

    _poll() {
        if (this._stopped) return
        if (!this.hasConversationIdValue || !this.hasUrlValue) return

        // A background task (AgentRun) legitimately works for an hour or more;
        // its own heartbeat sweep, not this cap, decides when it is dead.
        if (!this._runLive && Date.now() - this._startedAt > MAX_POLL_MS) {
            this._refreshChatFrame({ stop: true })
            return
        }

        // A hidden tab has no reader to show a reply to. Stop spending
        // fetches on it — _onVisibility re-arms and calls _poll() directly
        // the instant it's looked at again, rather than waiting out whatever
        // delay was already queued.
        if (document.hidden) {
            this._pausedForVisibility = true
            return
        }

        fetch(`${this.urlValue}?conversation_id=${this.conversationIdValue}`, {
            headers: { Accept: "application/json" },
            credentials: "same-origin"
        })
            .then((r) => {
                // A non-2xx status (a 401 from a session that expired mid-poll,
                // a 500) used to fall through to the catch below and retry
                // silently for up to MAX_POLL_MS. Same for a same-origin
                // redirect to a non-JSON page (also the expired-session case,
                // just via a 200) — r.json() on an HTML body would otherwise
                // raise a SyntaxError that reads exactly like a network blip.
                // Neither is retryable, so both are terminal here.
                if (!r.ok || (r.redirected && !(r.headers.get("Content-Type") || "").includes("application/json"))) {
                    throw new TerminalPollError(`poll response not usable (status ${r.status}, redirected ${r.redirected})`)
                }
                return r.json()
            })
            .then((data) => {
                if (this._stopped) return
                this._runLive = !!(data && data.run_live)
                if (data && data.ok && data.done) {
                    this._refreshChatFrame({ stop: true })
                    return
                }
                // Re-render mid-run whenever new turns land or the pending
                // reply streams more text, so progress is visible live.
                if (data && data.ok && data.fingerprint && data.fingerprint !== this._fingerprint) {
                    this._fingerprint = data.fingerprint
                    this._refreshChatFrame({ stop: false })
                }
                // A long background run changes every few seconds at most.
                let delay
                if (this._waitingForFirstWords() && this._waitTicks < WAIT_TICKS) {
                    this._waitTicks += 1
                    delay = WAIT_POLL_MS
                } else {
                    this._ticks += 1
                    delay = this._ticks < FAST_TICKS ? FAST_POLL_MS : (this._runLive ? RUN_POLL_MS : SLOW_POLL_MS)
                }
                this._timer = setTimeout(() => this._poll(), delay)
            })
            .catch((err) => {
                if (this._stopped) return
                if (err instanceof TerminalPollError) {
                    // Stop rather than retry: nothing about the next tick's
                    // fetch would come back any differently, and the reader
                    // deserves to know the reply they're waiting on isn't
                    // coming without a reload.
                    this._stopped = true
                    this._unsubscribeActivity()
                    showSystemToast("Lost the connection to Jane — reload to keep going", { level: "danger", duration: 0 })
                    return
                }
                // A genuine network error (offline, DNS hiccup) is worth
                // retrying — the same transient backoff as before.
                this._timer = setTimeout(() => this._poll(), SLOW_POLL_MS)
            })
    }

    // A reply is in flight and nothing of it has been written yet: the pending
    // bubble still shows its typing indicator (_turns.html.slim renders that
    // only for the "Responding…" / "Searching the web…" placeholders).
    _waitingForFirstWords() {
        return !!this.element.querySelector("[data-assistant-pending='true'] .typing-indicator")
    }

    _hasPending() {
        return !!this.element.querySelector("[data-assistant-pending='true']")
    }

    // Where the pane sits. A reply in flight means the message the reader just
    // sent is the thing to look at, so pin THAT to the top and let the answer
    // grow into the space below (utils/chat_scroll explains why, and why this
    // beats scrolling to the bottom for long answers). With nothing in flight
    // the thread is history and belongs at its end, which is also where a
    // reader resuming an old conversation expects to land.
    //
    // Sending a message is a full Turbo.visit, so this runs on a fresh DOM with
    // the new user turn and its pending bubble already rendered — the send case
    // and the "reply arrived while I was away" case are the same code path.
    _settleScroll({ reset = false } = {}) {
        const scroller = this._scroller()
        if (!scroller) return

        if (!this._chatScroll || this._chatScrollFor !== scroller) {
            this._chatScroll?.destroy()
            this._chatScrollFor = scroller
            this._chatScroll = attachChatScroll(scroller, { anchor: "top" })
        }

        const lastUser = this._lastUserTurn(scroller)
        const lastUserId = lastUser?.dataset?.turnId || null

        // A fresh DOM (page load, or a send, which is a full Turbo.visit): pin
        // only if a reply is in flight. Otherwise the thread is history and
        // belongs at its end.
        if (reset) {
            const pinning = this._hasPending() ? lastUser : null
            this._anchoredTurnId = pinning ? lastUserId : null
            this._chatScroll.anchorTo(pinning).reset()
            return
        }

        // A NEW user turn appeared without a reload — the Jane sidebar composes
        // by fetch — so re-arm the pin on it.
        if (lastUserId && lastUserId !== this._anchoredTurnId && this._hasPending()) {
            this._anchoredTurnId = lastUserId
            this._chatScroll.anchorTo(lastUser).reset()
            return
        }

        // Otherwise the anchor STAYS whatever it already was. Re-deriving it
        // from _hasPending() here is what made every reply end in a lurch: the
        // moment the placeholder flipped to "done" the pin was dropped, the
        // spacer collapsed and the pane slammed to the bottom — measured at 959px
        // of unrequested movement, once per message. Finishing a reply is not a
        // scroll event. The pin is released by the reader scrolling, or replaced
        // by the next thing they send, and by nothing else.
        //
        // The node itself is re-resolved by turn id because a poll re-render
        // replaces the whole chat partial, leaving the previous element detached.
        if (this._anchoredTurnId) {
            this._chatScroll.anchorTo(scroller.querySelector(`.assistant-bubble-user[data-turn-id="${this._anchoredTurnId}"]`))
        }
        this._chatScroll.contentChanged()
    }

    _lastUserTurn(scroller) {
        const bubbles = scroller.querySelectorAll(".assistant-bubble-user")
        return bubbles[bubbles.length - 1] || null
    }

    // Full-page chat scrolls .assistant-content; the sidebar detail panel
    // scrolls .msg-detail-scroll.
    _scroller() {
        return this.element.querySelector(".assistant-content") || this.element.querySelector(".msg-detail-scroll")
    }

    _refreshChatFrame({ stop } = { stop: true }) {
        if (this._stopped) return
        if (stop) {
            this._stopped = true
            this._unsubscribeActivity()
        }
        // A re-render replaces the whole chat partial, and an open reaction
        // picker with it — mid-search, mid-scroll through categories. Hold the
        // refresh until the picker closes instead; nothing in the thread is
        // lost by showing it a moment later, and a later tick's refresh folds
        // into the same held one.
        if (this._reactionPickerOpen()) {
            this._deferredRefresh = { stop: stop || !!this._deferredRefresh?.stop }
            return
        }
        this._fetchChatFrame(stop)
    }

    _reactionPickerOpen() {
        return !!this.element.querySelector(".rxp-menu:not([hidden]), .rxp-full:not([hidden])")
    }

    _flushDeferredRefresh() {
        const held = this._deferredRefresh
        if (!held) return
        this._deferredRefresh = null
        this._fetchChatFrame(held.stop)
    }

    _fetchChatFrame(stop) {
        if (!this.hasFrameUrlValue) {
            if (stop) window.location.reload()
            return
        }
        fetch(`${this.frameUrlValue}?conversation_id=${this.conversationIdValue}`, {
            headers: { Accept: "text/vnd.turbo-stream.html" },
            credentials: "same-origin"
        })
            .then((r) => r.text())
            .then((html) => {
                if (window.Turbo && typeof window.Turbo.renderStreamMessage === "function") {
                    const cardState = this._snapshotCardState()
                    this._keepComposersThroughRender()
                    window.Turbo.renderStreamMessage(html)
                    requestAnimationFrame(() => {
                        this._restoreCardState(cardState)
                        // After the swap, not before: the anchor element is a
                        // brand-new node and the reply has just grown.
                        this._settleScroll()
                    })
                } else if (stop) {
                    window.location.reload()
                }
            })
            .catch(() => {
                if (stop) window.location.reload()
            })
    }

    // An editable card - the composer (assistants/_outgoing_draft_card), the
    // calendar invite (live/_calendar_invite_card) - is a live editor: replacing
    // it mid-sentence would drop the caret and anything not saved yet. So a
    // card marked data-live-card keeps its own node through the re-render
    // whenever it has unsaved edits (data-live-card-dirty), holds focus, or the
    // server's copy is no newer than the one on screen (data-live-card-version).
    // A newer server copy of a card nobody is touching - Sent, after the Undo
    // window - still comes in.
    //
    // The swap happens inside Turbo's stream render (turbo:before-stream-render
    // wraps it), so the old node goes straight back with no frame in between.
    _keepComposersThroughRender() {
        const kept = new Map()
        this.element.querySelectorAll("[data-live-card]").forEach((node) => {
            kept.set(node.dataset.liveCard, node)
        })
        if (kept.size === 0) return
        const active = document.activeElement
        const selection = window.getSelection()
        const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0).cloneRange() : null
        const caret = active && "selectionStart" in active ? [active.selectionStart, active.selectionEnd] : null
        const onRender = (event) => {
            // Only the chat frame's own update; a tray or toast stream rendering
            // at the same moment passes through untouched.
            if (event.target?.getAttribute?.("target") !== "assistant_chat") return
            document.removeEventListener("turbo:before-stream-render", onRender)
            const render = event.detail.render
            event.detail.render = async (streamElement) => {
                await render(streamElement)
                let restored = false
                this.element.querySelectorAll("[data-live-card]").forEach((fresh) => {
                    const old = kept.get(fresh.dataset.liveCard)
                    if (!old || old === fresh) return
                    const busy = old.dataset.liveCardDirty === "true" || old.contains(active)
                    const serverNewer = Number(fresh.dataset.liveCardVersion) > Number(old.dataset.liveCardVersion)
                    if (!busy && serverNewer) return
                    fresh.replaceWith(old)
                    restored = restored || old.contains(active)
                })
                if (restored && active?.isConnected) {
                    active.focus({ preventScroll: true })
                    if (caret) {
                        try { active.setSelectionRange(caret[0], caret[1]) } catch (_e) { /* not a text input */ }
                    } else if (range && active.contains(range.startContainer)) {
                        selection.removeAllRanges()
                        selection.addRange(range)
                    }
                }
            }
        }
        document.addEventListener("turbo:before-stream-render", onRender)
        setTimeout(() => document.removeEventListener("turbo:before-stream-render", onRender), 5000)
    }

    // Mid-run re-renders replace the whole chat partial; without this, any
    // checkboxes the user unticked on a proposal card (or an open split menu,
    // or a tool card the user expanded/collapsed) would snap back to defaults.
    _snapshotCardState() {
        const state = { checks: new Map(), openMenus: new Set(), toolCards: new Map(), openProposalMenus: new Set() }
        try {
            this.element.querySelectorAll("input[name='message_ids[]']").forEach((input) => {
                state.checks.set(input.value, input.checked)
            })
            this.element.querySelectorAll("details[data-tool-turn-id]").forEach((card) => {
                state.toolCards.set(card.dataset.toolTurnId, card.open)
            })
            this.element.querySelectorAll("form").forEach((form, index) => {
                if (!form.querySelector(".tool-split-menu.is-open")) return
                const key = form.querySelector("input[name='proposal_turn_id']")?.value || `form-${index}`
                state.openMenus.add(key)
            })
            // Jane Live proposal cards use a different dropdown (.tool-action-menu
            // inside a [data-tool-turn-id] tool card). Key the open menu by its
            // turn id so a background poll re-render doesn't snap it shut.
            this.element.querySelectorAll(".tool-action-menu.is-open").forEach((menu) => {
                const turnId = menu.closest("[data-tool-turn-id]")?.dataset.toolTurnId
                if (turnId) state.openProposalMenus.add(turnId)
            })
        } catch (_e) { /* best-effort snapshot */ }
        return state
    }

    _restoreCardState(state) {
        if (!state) return
        try {
            this.element.querySelectorAll("input[name='message_ids[]']").forEach((input) => {
                if (state.checks.has(input.value)) input.checked = state.checks.get(input.value)
            })
            this.element.querySelectorAll("details[data-tool-turn-id]").forEach((card) => {
                if (state.toolCards.has(card.dataset.toolTurnId)) card.open = state.toolCards.get(card.dataset.toolTurnId)
            })
            this.element.querySelectorAll("form").forEach((form, index) => {
                const key = form.querySelector("input[name='proposal_turn_id']")?.value || `form-${index}`
                if (!state.openMenus.has(key)) return
                const menu = form.querySelector(".tool-split-menu")
                const caret = form.querySelector(".tool-split-caret")
                if (menu) menu.classList.add("is-open")
                if (caret) caret.setAttribute("aria-expanded", "true")
            })
            if (state.openProposalMenus && state.openProposalMenus.size) {
                this.element.querySelectorAll("[data-tool-turn-id]").forEach((card) => {
                    if (!state.openProposalMenus.has(card.dataset.toolTurnId)) return
                    const menu = card.querySelector(".tool-action-menu")
                    const caret = card.querySelector(".tool-action--split-caret")
                    if (menu) menu.classList.add("is-open")
                    if (caret) caret.setAttribute("aria-expanded", "true")
                })
            }
        } catch (_e) { /* best-effort restore */ }
    }

}

